/**
 * Fuente única de nombres y descripciones de navegación (Anexo E).
 *
 * Se indexa por `id` de entrada o de espacio del registro
 * (`src/components/layout/navegacion/registro.ts`). El registro guarda la
 * estructura, los permisos y las direcciones; ESTO solo define cómo se le habla
 * al empleado. Una prueba exige correspondencia uno a uno entre los ids del
 * registro y las claves de acá.
 *
 * Reglas (decididas con Denis 2026-05-30 y 2026-10-24):
 *  - "Leads" se mantiene (lenguaje de marketing aceptado).
 *  - Palabras del negocio: nada de Admin, Hub, Config ni jerga de dev.
 *  - Nada de IDs internos en `label`/`description`.
 */

export interface NavMeta {
  /** Nombre amigable mostrado al usuario. */
  label: string;
  /** Una frase: qué es + para qué sirve. */
  description: string;
}

export const NAV_META: Record<string, NavMeta> = {
  // — Arriba de todo —
  inicio: {
    label: 'Inicio',
    description: 'Tu resumen del día: pendientes y actividad reciente.',
  },
  sol: {
    label: 'Sol',
    description: 'Tus conversaciones con Sol, el asistente de IA, con buscador.',
  },
  chat: {
    label: 'Chat',
    description: 'Mensajería interna del equipo, por canales y mensajes directos.',
  },

  // — Comercial —
  ventas: {
    label: 'Ventas',
    description: 'Todo lo que pasa antes de cerrar una venta.',
  },
  prospectos: {
    label: 'Prospectos',
    description: 'Empresas y personas a las que les querés vender.',
  },
  leads: {
    label: 'Leads',
    description: 'Prospectos que respondieron y están en conversación.',
  },
  casos: {
    label: 'Casos de éxito',
    description: 'Lo que resolvimos con cada cliente ganado, para usar al vender.',
  },
  marketing: {
    label: 'Marketing',
    description: 'Lo que sale hacia afuera: campañas, newsletter y sus reglas.',
  },
  campanas: {
    label: 'Campañas',
    description: 'Secuencias de correo y publicaciones en redes que salen solas.',
  },
  newsletter: {
    label: 'Newsletter',
    description: 'Envíos periódicos a tu lista de contactos.',
  },
  'reglas-por-red': {
    label: 'Reglas por red',
    description: 'Límite de caracteres y tono de cada red social.',
  },

  // — Entrega —
  proyectos: {
    label: 'Proyectos',
    description: 'Espacios de trabajo con etapas, documentos, soporte y tareas.',
  },
  tareas: {
    label: 'Tareas',
    description: 'Lo que tenés que hacer, tuyo y del equipo.',
  },
  backlogs: {
    label: 'Backlogs',
    description: 'Lo pendiente de los repos que no tienen proyecto en Core.',
  },

  // — Negocio —
  clientes: {
    label: 'Clientes',
    description:
      'Lo que pasa después de vender: el abono del portal, los sistemas que entregamos y el soporte.',
  },
  partners: {
    label: 'Partners',
    description: 'El abono de cada cliente del portal: entradas, períodos, tickets y reportes.',
  },
  entornos: {
    label: 'Entornos de clientes',
    description: 'Los sistemas que entregamos: estado, usuarios, configuración y llaves de cada uno.',
  },
  'metricas-soporte': {
    label: 'Métricas de soporte',
    description: 'Cuánto tardamos en responder y entregar los pedidos de soporte, por persona.',
  },
  finanzas: {
    label: 'Finanzas',
    description: 'La plata: lo que se cobra, lo que se factura y la configuración para hacerlo.',
  },
  cobranzas: {
    label: 'Cobranzas',
    description: 'Cartera, cobros, comisiones y liquidaciones.',
  },
  contratos: {
    label: 'Contratos',
    description: 'Los acuerdos firmados: documentos, montos y pagos.',
  },
  facturacion: {
    label: 'Facturación',
    description: 'Las facturas emitidas por ARCA.',
  },
  gastos: {
    label: 'Gastos fijos',
    description: 'Los servicios que paga la empresa, con qué cuenta y cuánto margen deja cada cliente.',
  },
  arca: {
    label: 'ARCA',
    description: 'Certificado, clave y punto de venta para facturar.',
  },
  'cuentas-para-cobrar': {
    label: 'Cuentas para cobrar',
    description: 'Las cuentas donde te pagan los clientes y los métodos de pago que cuenta la IA.',
  },

  // — Configuración —
  empresa: {
    label: 'Empresa',
    description: 'Los datos de DenDev que usan todas las pantallas y la IA.',
  },
  'datos-empresa': {
    label: 'Datos de la empresa',
    description: 'Identidad, contactos y horarios de la empresa.',
  },
  catalogo: {
    label: 'Catálogo',
    description: 'Planes, servicios y productos para cotizar.',
  },
  ia: {
    label: 'Inteligencia artificial',
    description: 'Todo lo de IA de la empresa en un solo lugar. Lo personal queda en Ajustes.',
  },
  'ia-panel': {
    label: 'Panel',
    description: 'Cómo vienen funcionando los agentes en las últimas 24 horas.',
  },
  'ia-agentes': {
    label: 'Agentes',
    description: 'Cada agente con su prompt, sus reglas y sus registros.',
  },
  'ia-modelos': {
    label: 'Modelos',
    description: 'Qué modelo usa cada tarea, su respaldo y el catálogo.',
  },
  'ia-reglas': {
    label: 'Reglas y bloqueados',
    description: 'Lo que la IA nunca puede decir o hacer, y a quién no le escribe.',
  },
  'ia-contexto': {
    label: 'Contexto de la empresa',
    description: 'Lo que la IA sabe de DenDev para hablar en su nombre.',
  },
  'ia-telegram': {
    label: 'Telegram',
    description: 'El asistente de Telegram y los ajustes globales de IA.',
  },
  'ia-actividad': {
    label: 'Actividad de Sol',
    description: 'Las conversaciones de todos con Sol y las herramientas que usó.',
  },
  personas: {
    label: 'Personas',
    description: 'Quién trabaja acá y qué puede hacer cada uno.',
  },
  equipo: {
    label: 'Equipo',
    description: 'Directorio, distribución, áreas y cargos.',
  },
  'cuentas-y-roles': {
    label: 'Cuentas y roles',
    description: 'Invitar personas, darles acceso y asignar roles.',
  },
  sistema: {
    label: 'Sistema',
    description: 'El funcionamiento de la plataforma.',
  },
  infraestructura: {
    label: 'Infraestructura',
    description: 'Estado de Core, Supabase, Cloudflare y AWS, eventos y avisos.',
  },
  auditoria: {
    label: 'Auditoría',
    description: 'Quién hizo qué y cuándo.',
  },
  copias: {
    label: 'Copias de seguridad',
    description: 'Respaldos de la base de datos.',
  },
  privacidad: {
    label: 'Privacidad',
    description: 'Borrado de datos personales a pedido, retención de 24 meses e historial.',
  },

  // — Cuenta —
  ajustes: {
    label: 'Ajustes',
    description: 'Tu perfil, seguridad, notificaciones, tu IA, tu Telegram y tu correo.',
  },
  ayuda: {
    label: 'Ayuda',
    description: 'Glosario y guía de cada sección.',
  },
};

/** Nombre de una entrada o espacio por id, con respaldo al texto dado. */
export function navLabel(id: string, fallback: string): string {
  return NAV_META[id]?.label ?? fallback;
}

/** Descripción de una entrada o espacio por id (o undefined si no hay). */
export function navDescription(id: string): string | undefined {
  return NAV_META[id]?.description;
}
